import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Lock, ArrowRight } from 'lucide-react';
import { MembershipTier } from '../../types.ts';
import { useTranslation } from '../../i18n.ts';
import { Modal } from '../ui/Modal.tsx';

interface PricingData {
  currency: string;
  pricing: {
    pro: { monthly: number; annual: number };
    business: { monthly: number; annual: number };
  };
  campaigns: Array<{ discount_percent: number; affected_tiers: string[]; banner_text?: string }>;
}

interface UpgradePromptProps {
  requiredTier: MembershipTier;
  feature: string;
  variant?: 'inline' | 'banner' | 'modal';
  onUpgrade?: () => void;
  onClose?: () => void;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  requiredTier,
  feature,
  variant = 'inline',
  onUpgrade,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pricingData, setPricingData] = useState<PricingData | null>(null);

  useEffect(() => {
    fetch('/api/config/pricing')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setPricingData(data);
      })
      .catch((err) => console.warn('fetch pricing config failed:', err.message || err));
  }, []);

  // Escape key handler for modal variant
  const handleClose = useCallback(() => {
    if (onClose) onClose();
    else if (onUpgrade) onUpgrade();
  }, [onClose, onUpgrade]);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/account');
    }
  };

  // #1730 — Onderzoeker / Professional
  const tierLabel = t(`tierName_${requiredTier}`);

  // Calculate pricing display
  const getPricingDisplay = () => {
    if (!pricingData) return null;
    const tierPricing = (
      pricingData.pricing as Record<string, { monthly: number; annual: number }>
    )[requiredTier];
    if (!tierPricing) return null;
    const campaign = pricingData.campaigns.find((c) => c.affected_tiers.includes(requiredTier));
    const symbol =
      pricingData.currency === 'EUR' ? '\u20AC' : pricingData.currency === 'GBP' ? '\u00A3' : '$';
    if (campaign) {
      const discount = campaign.discount_percent / 100;
      const discountedMonthly = (tierPricing.monthly * (1 - discount)).toFixed(2);
      return {
        original: `${symbol}${tierPricing.monthly}`,
        discounted: `${symbol}${discountedMonthly}`,
        banner: campaign.banner_text,
      };
    }
    return { price: `${symbol}${tierPricing.monthly}${t('rpPerMonthShort')}` };
  };

  const priceInfo = getPricingDisplay();

  if (variant === 'inline') {
    return (
      <button
        onClick={handleUpgrade}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pt-forest/5 text-pt-forest/40 text-xs font-bold hover:bg-pt-gold/10 hover:text-pt-gold-text hover:dark:text-[var(--gold-text)] transition-all cursor-pointer"
        title={`${t('upgradeRequired')} — ${tierLabel}`}
      >
        <Lock size={12} />
        <span>{tierLabel}</span>
      </button>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="space-y-2">
        {priceInfo && 'banner' in priceInfo && priceInfo.banner && (
          <div className="px-4 py-2 rounded-xl bg-pt-gold/10 border border-pt-gold/30 text-xs font-bold text-pt-forest text-center">
            {priceInfo.banner}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-pt-forest/5 to-pt-gold/5 border border-pt-gold/20 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pt-gold/10 flex items-center justify-center">
              <Crown size={18} className="text-pt-gold-text dark:text-[var(--gold-text)]" />
            </div>
            <div>
              <p className="text-sm font-bold text-pt-forest">{feature}</p>
              <p className="text-xs text-pt-sage-text">
                {t('upgradeToUnlock', { tier: tierLabel })}
                {priceInfo && 'discounted' in priceInfo ? (
                  <>
                    {' '}
                    —{' '}
                    <span className="line-through opacity-50">
                      {priceInfo.original}
                      {t('rpPerMonthShort')}
                    </span>{' '}
                    <span className="text-pt-gold-text dark:text-[var(--gold-text)] font-bold">
                      {priceInfo.discounted}
                      {t('rpPerMonthShort')}
                    </span>
                  </>
                ) : priceInfo && 'price' in priceInfo ? (
                  <> — {priceInfo.price}</>
                ) : null}
              </p>
            </div>
          </div>
          <button
            onClick={handleUpgrade}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-pt-gold text-pt-forest text-xs font-black uppercase tracking-widest hover:scale-[1.02] transition-all"
          >
            {t('upgrade')} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  // modal variant
  return (
    <Modal isOpen={true} onClose={handleClose} titleId="modal-title-upgrade" size="sm">
      <div className="p-6 md:p-12 space-y-8">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-2xl bg-pt-gold/10 flex items-center justify-center">
            <Crown size={32} className="text-pt-gold-text dark:text-[var(--gold-text)]" />
          </div>
        </div>
        <div className="text-center space-y-3">
          <h3
            id="modal-title-upgrade"
            className="text-2xl font-serif font-bold text-pt-forest dark:text-[var(--text-main)]"
          >
            {t('upgradeRequired')}
          </h3>
          <p className="text-sm text-pt-sage-text dark:text-[var(--text-muted)]">
            {t('upgradeToUnlock', { tier: tierLabel })}
          </p>
          <p className="text-xs text-pt-sage-text dark:text-[var(--text-muted)] font-medium">
            {feature}
          </p>
        </div>
        <button
          onClick={handleUpgrade}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-pt-gold text-pt-forest font-black uppercase tracking-widest text-xs hover:scale-[1.02] transition-all"
        >
          {t('viewPlans')} <ArrowRight size={14} />
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="w-full py-3 text-xs font-bold text-pt-sage-text dark:text-[var(--text-muted)] hover:text-pt-forest dark:hover:text-[var(--text-main)] transition-colors uppercase tracking-widest"
          >
            {t('cancel')}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default UpgradePrompt;
